import { forwardRef, useCallback, useEffect, useId, useImperativeHandle, useRef, type ChangeEvent, type TextareaHTMLAttributes } from 'react';
import { cn } from '../../utils/cn';
import { useControllableState } from '../../hooks/useControllableState';
import { inputBase, inputInvalid, useFieldA11y, type FieldControlProps } from '../shared/fieldStyles';

export interface TextareaProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'defaultValue' | 'rows'>,
    Omit<FieldControlProps, 'size'> {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  /** Grows with content between `minRows` and `maxRows`. */
  autoResize?: boolean;
  minRows?: number;
  maxRows?: number;
  /** Monospace text (notes with codes, JSON). */
  mono?: boolean;
  /** Hide the character counter that appears with `maxLength`. */
  hideCounter?: boolean;
  /** Disable manual resizing handle (default: vertical resize allowed unless autoResize). */
  resizable?: boolean;
}

const sizeClasses = 'px-control-x py-1.5 text-base leading-normal';

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  {
    value,
    defaultValue,
    onChange,
    onValueChange,
    autoResize,
    minRows = 3,
    maxRows,
    mono,
    hideCounter,
    resizable,
    className,
    invalid,
    readOnly,
    disabled,
    maxLength,
    id,
    'aria-describedby': ariaDescribedBy,
    ...rest
  },
  ref,
) {
  const areaRef = useRef<HTMLTextAreaElement>(null);
  useImperativeHandle(ref, () => areaRef.current as HTMLTextAreaElement);
  const reactId = useId();
  const counterId = `${id ?? reactId}-counter`;
  const [current, setCurrent] = useControllableState<string>({ value, defaultValue: defaultValue ?? '', onChange: onValueChange });
  const showCounter = typeof maxLength === 'number' && !hideCounter;
  const a11y = useFieldA11y({ invalid, 'aria-describedby': ariaDescribedBy, describedBy: showCounter ? counterId : undefined });

  const resize = useCallback(() => {
    const el = areaRef.current;
    if (!el || !autoResize) return;
    const styles = window.getComputedStyle(el);
    const lineHeight = parseFloat(styles.lineHeight) || 20;
    const padding = (parseFloat(styles.paddingTop) || 0) + (parseFloat(styles.paddingBottom) || 0);
    const border = (parseFloat(styles.borderTopWidth) || 0) + (parseFloat(styles.borderBottomWidth) || 0);
    el.style.height = 'auto';
    const min = minRows * lineHeight + padding + border;
    const max = maxRows ? maxRows * lineHeight + padding + border : Infinity;
    const next = Math.min(max, Math.max(min, el.scrollHeight + border));
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight + border > max ? 'auto' : 'hidden';
  }, [autoResize, minRows, maxRows]);

  useEffect(() => {
    resize();
  }, [resize, current]);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setCurrent(e.target.value);
    onChange?.(e);
  };

  const remaining = typeof maxLength === 'number' ? maxLength - current.length : 0;
  const resizeClass = autoResize ? 'resize-none' : resizable === false ? 'resize-none' : 'resize-y';

  return (
    <div className="zo-textarea relative flex w-full flex-col">
      <textarea
        ref={areaRef}
        id={id}
        rows={minRows}
        value={current}
        onChange={handleChange}
        disabled={disabled}
        readOnly={readOnly}
        maxLength={maxLength}
        className={cn(inputBase, sizeClasses, 'h-auto scrollbar-thin', resizeClass, mono && 'font-mono tnum', invalid && inputInvalid, className)}
        {...a11y}
        {...rest}
      />
      {showCounter ? (
        <div
          id={counterId}
          className={cn('mt-1 self-end type-caption tnum', remaining < 0 ? 'text-danger-fg' : remaining <= Math.ceil(maxLength * 0.1) ? 'text-warning-fg' : 'text-fg-subtle')}
          aria-live="polite"
        >
          {current.length} / {maxLength}
        </div>
      ) : null}
    </div>
  );
});
